import * as React from "react";
import {MathUtils as THREEMath} from "three";
import styled from "styled-components";
import type {IRGBObject} from "../../../../../utils/ColorUtils";
import {ColorUtils} from "../../../../../utils/ColorUtils";
import type {Color, PointDouble} from "../../../../../generated/api/base";
import {ReactUtils} from "../../../../utils/ReactUtils";
import {HTMLUtils} from "../../../../../utils/HTML/HTMLUtils";
import {HorizontalAlignment} from "../../../../../utils/dom/DomUtils";
import {Constants} from "../../../../modules/space/spaceeditor/logic3d/Constants";
import {TextInputV5} from "../../../details/datatypes/TextInputV5";
import type {IconLayer} from "./IconEditorV5";
import {IconImageV5} from "./IconImageV5";
import {IconTextV5} from "./IconTextV5";

interface IEditableIconProps {
	readonly isSVG: boolean;
	readonly innerPart: string;
	readonly imageAspectRatio: number;
	readonly backgroundColor: Color;
	readonly isIconColorChanged: boolean;
	readonly iconColor: Color;
	readonly iconColors: Color[];
	readonly selectedChildIndex: number;
	readonly replaceColor: boolean;
	readonly borderRadius: number;
	readonly scale: number;
	readonly isFlippedX: boolean;
	readonly isFlippedY: boolean;
	readonly iconTranslate: PointDouble; // normalized to [0, 1]
	readonly iconOrientation: number;

	readonly text: string;
	readonly textOrientation: number;
	readonly textTranslate: PointDouble; // normalized to [0, 1]
	readonly fontSize: number;
	readonly fontColor: Color;
	readonly fontFamily: string;
	readonly isBold: boolean;
	readonly isItalic: boolean;
	readonly isUnderlined: boolean;
	readonly horizontalAlignment: HorizontalAlignment;

	readonly selectedLayer?: IconLayer;
	readonly outline?: boolean;

	readonly classNames?: string;
	readonly onClick?: (innerPart: string) => void;

	readonly onSelectedChildIndexChange?: (newIndex: number) => void;

	// below ones are for the IconEditor
	readonly onIconTranslateChange?: (newTranslate: PointDouble) => void;
	readonly onIconOrientationChange?: (newOrientation: number) => void;
	readonly onTextTranslateChange?: (newTranslate: PointDouble) => void;
	readonly onTextOrientationChange?: (newOrientation: number) => void;
	readonly onTextChange?: (newText: string) => void;
	readonly isTextBeingModified?: boolean;
	readonly onIsTextBeingModifiedChange?: (value: boolean) => void;
	readonly svgRef?: React.RefObject<SVGSVGElement>;
	readonly title?: string;
}

export class EditableIconV5 extends React.Component<IEditableIconProps> {
	private _svgRef: React.RefObject<SVGSVGElement>;
	private _borderColorThreshold = 180;

	constructor(props: IEditableIconProps) {
		super(props);
		this._svgRef = this.props.svgRef || React.createRef<SVGSVGElement>();
	}

	private onTextInputBlur = () => {
		this.props.onIsTextBeingModifiedChange?.(false);
	};

	private onClick = () => {
		if (this.props.onClick) {
			this.props.onClick(this.props.innerPart);
		}
	};

	private getBorderColor = () => {
		const defaultBorderColor = "#3495F0";

		const {r, g, b} = ColorUtils.hex2rgb(this.props.backgroundColor.hex, 1, "RGBObject") as IRGBObject;

		const {r: defR, g: defG, b: defB} = ColorUtils.hex2rgb(defaultBorderColor, 1, "RGBObject") as IRGBObject;

		if (Math.abs(r - defR) + Math.abs(g - defG) + Math.abs(b - defB) >= this._borderColorThreshold) {
			return defaultBorderColor;
		} else {
			return ColorUtils.rgb2hex(255 - r, 255 - g, 255 - b, "string", true) as string;
		}
	};

	private getAlignment = () => {
		const {horizontalAlignment} = this.props;

		if (horizontalAlignment === HorizontalAlignment.left) {
			return "left";
		}
		if (horizontalAlignment === HorizontalAlignment.center) {
			return "center";
		}
		return "right";
	};

	private onDoubleClick = () => {
		if (this.props.selectedLayer === "Text") {
			return this.props.onIsTextBeingModifiedChange(true);
		}
	};

	public override render() {
		const {
			textOrientation,
			textTranslate,
			isTextBeingModified,
			fontColor,
			fontFamily,
			fontSize,
			isBold,
			isItalic,
			isUnderlined,
			classNames,
			borderRadius,
			isIconColorChanged,
			innerPart,
			imageAspectRatio,
			iconColor,
			iconColors,
			replaceColor,
			isSVG,
			iconTranslate,
			iconOrientation,
			scale,
			isFlippedX,
			isFlippedY,
			onIconOrientationChange,
			onIconTranslateChange,
			selectedLayer,
			text,
			backgroundColor,
			onTextTranslateChange,
			onTextOrientationChange,
			outline,
			title,
			onTextChange,
			horizontalAlignment,
			selectedChildIndex,
			onSelectedChildIndexChange,
		} = this.props;
		const bgColorAsRGB = ColorUtils.hex2rgb(backgroundColor.hex, 1 - backgroundColor.transparency) as string;
		const borderColor = this.getBorderColor();

		let inputStyle: React.CSSProperties = {};

		if (isTextBeingModified && this._svgRef.current) {
			const svgSize = HTMLUtils.getSize(this._svgRef.current);

			inputStyle = {
				position: "absolute",
				top: `${textTranslate.y * 100}%`,
				left: `${textTranslate.x * 100}%`,
				fontFamily: fontFamily,
				fontSize: fontSize,
				textAlign: this.getAlignment(),
				color: ColorUtils.hex2rgb(fontColor.hex, 1 - fontColor.transparency, "string") as string,
				fontWeight: isBold ? "bold" : "normal",
				fontStyle: isItalic ? "italic" : "normal",
				textDecoration: isUnderlined ? "underline" : "none",
				paddingBottom: `${(3 * fontSize) / 20}px`,
				transformOrigin: "center",
				transform: `translate(-50%, -50%) rotate(${THREEMath.radToDeg(textOrientation)}deg) scale(${svgSize.width / Constants.CATALOG_SVG_VIEWBOXSIZE})`,
				backgroundColor: "transparent",
				whiteSpace: "nowrap",
				overflowY: "auto",
			};
		}

		const isEditor = classNames.includes("IconEditor");

		const svg = (
			<svg
				xmlns={Constants.SVG_NAMESPACE}
				viewBox={`0 0 ${Constants.CATALOG_SVG_VIEWBOXSIZE} ${Constants.CATALOG_SVG_VIEWBOXSIZE}`}
				ref={this._svgRef}
				onDoubleClick={this.onDoubleClick}
				style={{cursor: this.props.selectedLayer === "Image" || this.props.selectedLayer === "Text" ? "move" : ""}}
			>
				<rect
					x={0}
					y={0}
					width={Constants.CATALOG_SVG_VIEWBOXSIZE}
					height={Constants.CATALOG_SVG_VIEWBOXSIZE}
					rx={borderRadius}
					ry={borderRadius}
					fill={bgColorAsRGB}
					stroke="none"
				/>
				<IconImageV5
					innerPart={innerPart}
					imageAspectRatio={imageAspectRatio}
					isIconColorChanged={isIconColorChanged}
					backgroundColor={backgroundColor}
					color={iconColor}
					colors={iconColors}
					selectedChildIndex={selectedChildIndex}
					onSelectedChildIndexChange={onSelectedChildIndexChange}
					replaceColor={replaceColor}
					isSVG={isSVG}
					viewBoxSize={Constants.CATALOG_SVG_VIEWBOXSIZE}
					translate={iconTranslate}
					orientation={iconOrientation}
					scale={scale}
					isFlippedX={isFlippedX}
					isFlippedY={isFlippedY}
					onTranslateChange={onIconTranslateChange}
					onOrientationChange={onIconOrientationChange}
					selectedLayer={selectedLayer}
					parentRef={this._svgRef}
					borderColor={borderColor}
				/>
				{!isTextBeingModified && (
					<IconTextV5
						x={textTranslate.x}
						y={textTranslate.y}
						viewBoxSize={Constants.CATALOG_SVG_VIEWBOXSIZE}
						orientation={textOrientation}
						text={text}
						onTranslateChange={onTextTranslateChange}
						onOrientationChange={onTextOrientationChange}
						isSelected={selectedLayer === "Text"}
						parentRef={this._svgRef}
						color={fontColor}
						fontFamily={fontFamily}
						fontSize={fontSize}
						isBold={isBold}
						isItalic={isItalic}
						isUnderlined={isUnderlined}
						borderColor={borderColor}
						horizontalAlignment={horizontalAlignment}
					/>
				)}
			</svg>
		);

		return (
			<div
				className={ReactUtils.cls(classNames, {outline: outline || isEditor})}
				onClick={this.onClick}
				title={title ? title : ""}
			>
				{isEditor ? <SVGContainerStyled>{svg}</SVGContainerStyled> : svg}
				{isEditor && isTextBeingModified && (
					<InvisibleDivStyled>
						<TextInputV5
							onBlur={this.onTextInputBlur}
							style={inputStyle}
							onChange={onTextChange}
							value={text}
							autoFocus={true}
							isTextArea={true}
							borderColor={borderColor}
							viewBoxSize={Constants.CATALOG_SVG_VIEWBOXSIZE}
						/>
					</InvisibleDivStyled>
				)}
			</div>
		);
	}
}

const InvisibleDivStyled = styled.div`
	::-webkit-scrollbar {
		width: 1px;
		height: 1px;
	}

	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	overflow: hidden;

	input {
		background: none;
		border: none;
		transition: none;
		padding: 0;
	}
`;

const SVGContainerStyled = styled.div`
	& > svg > g > g > g.areChildrenSelectable > * {
		cursor: pointer;

		&:not([selectedsvgpart="true"]):hover {
			filter: contrast(0.8);
		}
	}
`;
